import { useEffect, useRef, useState } from 'react';
import { Outlet, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Globe,
  Server,
  Network,
  LogOut,
  Settings,
  User,
  Menu,
  X,
  Wallet,
  Plus,
  ShieldCheck,
  Search,
  FileKey2,
  ShoppingBag,
  MessageSquare,
  BellRing,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { trackCTAClick } from '../lib/gtag';
import { CertiniteBrandLink } from './CertiniteBrand';
import DashboardLanguageSwitch from './DashboardLanguageSwitch';
import api from '../services/api';
import { RenewalAlertsProvider, useRenewalAlerts } from '../contexts/RenewalAlertsContext';
import { useWorkspace } from '../contexts/WorkspaceContext';

export default function Layout() {
  const { access, error, refresh } = useWorkspace();
  const { t } = useTranslation();
  if (!access) return <div className="p-8 text-center text-sm" role={error ? 'alert' : 'status'}>{t(error ? 'agency.accessError' : 'agency.loading')}{error && <button className="ml-3 underline" onClick={() => void refresh()}>{t('agency.retry')}</button>}</div>;
  return <RenewalAlertsProvider key={`${access.workspaceId}:${access.role}`}><LayoutContent /></RenewalAlertsProvider>;
}

function LayoutContent() {
  const { alerts, error: alertsError } = useRenewalAlerts();
  const { t, i18n } = useTranslation();
  const { logout, isAdmin } = useAuth();
  const { access, canManage, isOwner, select } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [walletOpen, setWalletOpen] = useState(false);
  const walletRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOwner) return;
    api.get('/wallet').then((res) => setWalletBalance(res.data?.balance ?? 0)).catch(() => {});
    const updateBalance = (event: Event) => {
      const balance = (event as CustomEvent<number>).detail;
      if (Number.isFinite(balance)) setWalletBalance(balance);
    };
    window.addEventListener('certinite:wallet-balance', updateBalance);
    return () => window.removeEventListener('certinite:wallet-balance', updateBalance);
  }, [location.pathname, isOwner]);

  useEffect(() => {
    if (!walletOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (walletRef.current && !walletRef.current.contains(e.target as Node)) {
        setWalletOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [walletOpen]);

  useEffect(() => {
    setWalletOpen(false);
  }, [location.pathname]);

  const navGroups = [
    {
      title: t('layout.navGeneral'),
      items: [
        { name: t('layout.dashboard'), path: '/dashboard', icon: LayoutDashboard },
        { name: t('agency.title'), path: '/dashboard/agency', icon: Users },
        { name: i18n.resolvedLanguage?.startsWith('tr') ? 'Kurallar ve onaylar' : 'Policies & approvals', path: '/dashboard/policies', icon: ShieldCheck },
      ],
    },
    {
      title: t('layout.navSsl'),
      items: [
        { name: t('layout.certificates'), path: '/dashboard/certificates', icon: FileKey2 },
        { name: i18n.resolvedLanguage?.startsWith('tr') ? 'Sertifika keşfi' : 'Certificate discovery', path: '/dashboard/discovery', icon: Search },
        { name: t('renewalAlerts.title'), path: '/dashboard/alerts', icon: BellRing },
        { name: t('monitor.title'), path: '/dashboard/ssl-monitor', icon: Globe },
        { name: t('layout.buySsl'), path: '/dashboard/ssl/buy', icon: ShoppingBag },
        { name: t('layout.sslOrders'), path: '/dashboard/ssl/orders', icon: ShieldCheck },
        { name: t('layout.domains'), path: '/dashboard/domains', icon: Globe },
      ],
    },
    {
      title: t('deployment.connections'),
      items: [
        { name: t('deployment.agentNav'), path: '/dashboard/agents', icon: Server },
        { name: t('deployment.gatewayNav'), path: '/dashboard/gateways', icon: Network },
      ],
    },
    {
      title: t('layout.navAccount'),
      items: [
        { name: t('layout.wallet'), path: '/dashboard/wallet', icon: Wallet },
        { name: t('layout.profile'), path: '/dashboard/profile', icon: User },
      ],
    },
  ];

  const pathAllowed = (path: string) => isOwner || (canManage && path === '/dashboard/ssl-monitor')
    || ['/dashboard/agency', '/dashboard/certificates', '/dashboard/alerts', '/dashboard/policies', '/dashboard/discovery'].includes(path);
  const pathKey = location.pathname.replace('/dashboard/', '').replace(/^\//, '') || 'dashboardOverview';
  const currentPageTitle =
    location.pathname === '/dashboard'
      ? t('layout.dashboardOverview')
      : pathKey === 'agents/install'
        ? t('deployment.agent.title')
        : pathKey === 'gateways/install'
          ? t('deployment.gateway.title')
        : pathKey === 'discovery'
          ? (i18n.resolvedLanguage?.startsWith('tr') ? 'Sertifika keşfi' : 'Certificate discovery')
        : pathKey === 'policies'
          ? (i18n.resolvedLanguage?.startsWith('tr') ? 'Kurallar ve onaylar' : 'Policies & approvals')
        : pathKey === 'agency'
          ? t('agency.title')
        : pathKey === 'ssl-monitor'
          ? t('monitor.title')
        : pathKey === 'ssl/buy'
          ? t('layout.buySsl')
          : pathKey === 'ssl/orders'
            ? t('layout.sslOrders')
            : t(`layout.${pathKey}`, { defaultValue: pathKey === 'gateways' ? 'Gateways · IIS' : pathKey });

  if (!pathAllowed(location.pathname)) return <Navigate to="/dashboard/agency" replace />;
  return (
    <div className="tenant-shell relative flex min-h-screen overflow-hidden antialiased text-[var(--tenant-text)] selection:bg-[var(--tenant-primary-soft)] selection:text-[var(--tenant-primary)]">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[2px] transition-opacity md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[15.5rem] shrink-0 transform flex-col border-r border-[var(--tenant-border)] bg-[var(--tenant-sidebar)] transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-[var(--tenant-border)] px-4">
          <CertiniteBrandLink to="/dashboard" variant="onLight" size="sm" className="min-w-0" />
          <button
            className="rounded-lg p-1.5 text-[var(--tenant-text-muted)] transition-colors hover:bg-[var(--tenant-nav-hover)] hover:text-[var(--tenant-title)] md:hidden"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close Sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="custom-scrollbar flex-1 space-y-5 overflow-y-auto px-3 py-4">
          <label className="block px-2 text-xs text-[var(--tenant-text-muted)]"><span>{t('agency.workspace')}</span><select aria-label={t('agency.workspace')} className="tenant-input mt-2 !text-xs" value={access!.workspaceId} onChange={e => select(e.target.value)}>{access!.workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select><span className="mt-2 block">{t(`agency.roles.${access!.role}`)}</span></label>
          {navGroups.map(g => ({ ...g, items: g.items.filter(i => pathAllowed(i.path)) })).filter(g => g.items.length > 0).map((group, groupIdx) => (
            <div key={groupIdx} className="space-y-1">
              <p className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--tenant-text-muted)]">
                {group.title}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path + '/'));
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => {
                        setIsSidebarOpen(false);
                        trackCTAClick('sidebar', item.path, item.name);
                      }}
                      className={`group flex items-center rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors duration-150 ${
                        isActive
                          ? 'bg-[var(--tenant-nav-active)] font-semibold text-[var(--tenant-primary)]'
                          : 'text-[var(--tenant-text-muted)] hover:bg-[var(--tenant-nav-hover)] hover:text-[var(--tenant-title)]'
                      }`}
                    >
                      <Icon
                        className={`mr-2.5 h-4 w-4 shrink-0 ${
                          isActive ? 'text-[var(--tenant-primary)]' : 'text-slate-400 group-hover:text-slate-500'
                        }`}
                      />
                      <span className="truncate">{item.name}</span>
                      {item.path === '/dashboard/alerts' && alerts.length > 0 && <span className={`ml-auto rounded-full px-1.5 text-[10px] ${alertsError ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-800'}`}>{alerts.length}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="mx-3 mb-2 rounded-xl border border-[var(--tenant-border)] bg-[var(--tenant-bg-subtle)] p-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
              <MessageSquare className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1">
                <p className="truncate text-xs font-semibold text-[var(--tenant-title)]">{t('layout.liveSupport')}</p>
                <span className="shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">
                  7/24
                </span>
              </div>
              <p className="truncate text-[11px] text-[var(--tenant-text-muted)]">{t('layout.liveSupportSub')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              trackCTAClick('sidebar', 'tawk', 'Live Support');
              if (!window.Tawk_API?.maximize) {
                toast.error(t('layout.liveSupportUnavailable'));
                return;
              }
              window.Tawk_API.showWidget?.();
              window.Tawk_API.maximize();
              setIsSidebarOpen(false);
            }}
            className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-500"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            <span>{t('layout.liveSupportChatNow')}</span>
          </button>
        </div>

        <div className="shrink-0 space-y-0.5 border-t border-[var(--tenant-border)] p-3">
          {isAdmin && (
            <Link
              to="/controlpanel"
              onClick={() => {
                setIsSidebarOpen(false);
                trackCTAClick('sidebar', '/controlpanel', 'Control Panel');
              }}
              className="flex w-full items-center rounded-lg px-2.5 py-2 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50"
            >
              <Settings className="mr-2.5 h-4 w-4 shrink-0" />
              <span className="truncate">{t('layout.controlPanel')}</span>
            </Link>
          )}
          <button
            onClick={logout}
            className="flex w-full items-center rounded-lg px-2.5 py-2 text-xs font-medium text-[var(--tenant-text-muted)] transition-colors hover:bg-rose-50 hover:text-rose-600"
          >
            <LogOut className="mr-2.5 h-4 w-4 shrink-0" />
            <span className="truncate">{t('layout.signOut')}</span>
          </button>
        </div>
      </aside>

      <main className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden bg-[var(--tenant-canvas)]">
        <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-[var(--tenant-border)] bg-[var(--tenant-header)] px-4 backdrop-blur-md md:px-7">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              className="shrink-0 rounded-lg p-1.5 text-[var(--tenant-text-muted)] transition-colors hover:bg-[var(--tenant-nav-hover)] hover:text-[var(--tenant-title)] focus:outline-none md:hidden"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Open Navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="max-w-[200px] truncate text-[15px] font-semibold tracking-tight text-[var(--tenant-title)] md:max-w-none md:text-base">
              {currentPageTitle}
            </h1>
          </div>

          <div className="flex shrink-0 items-center gap-2 md:gap-2.5">
            <DashboardLanguageSwitch />

            {isOwner && <><div className="relative" ref={walletRef}>
              <button
                type="button"
                onClick={() => {
                  setWalletOpen((o) => !o);
                  trackCTAClick('header', '/dashboard/wallet', 'Wallet');
                }}
                className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-[var(--tenant-border)] bg-white px-2.5 py-1.5 text-xs font-semibold text-[var(--tenant-title)] shadow-sm transition-colors hover:border-[var(--tenant-border-strong)] hover:bg-[var(--tenant-bg-subtle)] md:text-[13px]"
                aria-haspopup="true"
                aria-expanded={walletOpen}
                title={t('layout.wallet')}
              >
                <Wallet className="h-3.5 w-3.5 text-[var(--tenant-primary)]" />
                <span data-wallet-header-balance>{walletBalance !== null ? `$${walletBalance.toFixed(2)}` : '—'}</span>
              </button>

              {walletOpen && (
                <div className="absolute right-0 z-50 mt-2 w-60 origin-top-right overflow-hidden rounded-xl border border-[var(--tenant-border)] bg-white shadow-lg ring-1 ring-slate-900/5">
                  <div className="border-b border-[var(--tenant-border)] bg-[var(--tenant-bg-subtle)] px-4 py-3.5">
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--tenant-text-muted)]">
                      <Wallet className="h-3.5 w-3.5 text-[var(--tenant-primary)]" />
                      {t('wallet.balanceLabel')}
                    </div>
                    <p className="mt-1 text-xl font-semibold tracking-tight text-[var(--tenant-title)]">
                      {walletBalance !== null ? `$${walletBalance.toFixed(2)}` : '—'}
                    </p>
                  </div>
                  <div className="space-y-1.5 p-2.5">
                    <button
                      type="button"
                      onClick={() => {
                        setWalletOpen(false);
                        trackCTAClick('header', '/dashboard/wallet', 'AddFunds');
                        navigate('/dashboard/wallet');
                      }}
                      className="tenant-btn-primary w-full gap-2 py-2.5 text-sm"
                    >
                      <Plus className="h-4 w-4" />
                      {t('wallet.addFunds')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setWalletOpen(false);
                        navigate('/dashboard/wallet');
                      }}
                      className="tenant-btn-secondary w-full py-2 text-xs"
                    >
                      {t('wallet.manage')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/dashboard/profile"
              onClick={() => trackCTAClick('header', '/dashboard/profile', 'Profile')}
              className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-[var(--tenant-border)] bg-white px-2.5 py-1.5 text-xs font-semibold text-[var(--tenant-title)] shadow-sm transition-colors hover:border-[var(--tenant-border-strong)] hover:bg-[var(--tenant-bg-subtle)] md:text-[13px]"
            >
              <User className="h-3.5 w-3.5 text-[var(--tenant-primary)]" />
              <span className="hidden sm:inline">{t('layout.profile')}</span>
            </Link></>}
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4 scroll-smooth md:p-7">
          <div className="mx-auto h-full max-w-6xl">
            {location.pathname !== '/dashboard/alerts' && alerts.length > 0 && <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <span className="inline-flex items-center gap-2"><BellRing className="h-4 w-4 shrink-0" />{t('renewalAlerts.banner', { count: alerts.length })}</span>
              <Link to="/dashboard/alerts" className="font-semibold underline">{t('renewalAlerts.view')}</Link>
            </div>}
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
